// useLauncherDiscovery — what the launcher shows a person who has not found the
// assistant yet, and when it stops showing it.
//
// Two stores, because the facts have two lifetimes:
//
//   LOCAL (survives the browser closing) — `launcher-discovery`
//     opened    — the person has opened the assistant at least once
//     sessions  — how many page loads have mounted the launcher
//
//   SESSION (dies with the tab, like the login itself) — the auth session is on
//   the same backend, so "this session" lines up with "since you signed in"
//     launcher-hint-shown — the tip has already been shown this session
//
// None of it is personal data: a few flags, no tenant, no content. It is per
// browser, not per account, which is the accepted limit of a client-only hint.
//
// ─── THE HINT ──────────────────────────────────────────────────────────────
// Shown once per session, `HINT_DELAY_MS` after the page settles, for
// `HINT_VISIBLE_MS`, and only to someone who has never opened the assistant.
// Someone who has used it does not need telling. The tip is also marked as shown
// the moment it appears, so a refresh within the four seconds does not repeat it.
//
// ─── IT CLOSES BY ITSELF — THE POINTER DOES NOT HOLD IT OPEN ───────────────
// The countdown used to pause while the pointer was over the card and restart
// only on `mouseleave`. A pointer that happened to be resting where the card
// appears therefore kept it up indefinitely, until the person switched tabs
// (the pointer leaves the page, `mouseleave` finally fires, the countdown
// restarts). So only KEYBOARD FOCUS inside the card pauses it now: that is a
// deliberate act by someone mid-interaction with the close button, and `blur`
// (or Escape, or the ✕) always releases it. It can always be dismissed early.
//
// `compact` is when the pill's label collapses to the 48px circle: as soon as
// the tip is gone (it timed out, was dismissed, or had already been shown this
// session), or the person has opened the assistant once, or has loaded the CRM
// `COMPACT_AFTER_SESSIONS` times. The pill therefore stays wide only while it is
// still introducing itself — before the tip, and while the tip is up. The number
// is a starting guess, not a measured one.
//
// ─── THERE IS NO "HIDE" ────────────────────────────────────────────────────
// A hide control (with a restore button) briefly existed and was removed on
// request. The launcher is draggable instead (`useLauncherPosition`). The old
// `launcher-hidden` flag is deleted on load so nobody who pressed it is left
// with state nothing reads.

import { useCallback, useEffect, useRef, useState } from 'react';

import { createStorage } from '@fluxora/storage';

const local = createStorage('ai-assistant', { backend: 'local' });
const session = createStorage('ai-assistant');
const KEY = 'launcher-discovery';
const HINT_SHOWN_KEY = 'launcher-hint-shown';
const RETIRED_HIDDEN_KEY = 'launcher-hidden';

/** Page loads after which the label collapses even if the assistant never opened. */
export const COMPACT_AFTER_SESSIONS = 10;
/** Let the page settle before the hint appears, so it is not part of first paint. */
export const HINT_DELAY_MS = 1500;
/** How long the hint stays up, unless keyboard focus is inside it. */
export const HINT_VISIBLE_MS = 4000;

interface Discovery {
  readonly opened: boolean;
  readonly sessions: number;
}

const FRESH: Discovery = { opened: false, sessions: 0 };

function read(): Discovery {
  const raw = local.get<Partial<Discovery>>(KEY);
  if (raw === null || typeof raw !== 'object') return FRESH;
  return {
    opened: raw.opened === true,
    sessions: typeof raw.sessions === 'number' && raw.sessions >= 0 ? raw.sessions : 0,
  };
}

/** Storage can be full or blocked; the launcher then just behaves as a first visit. */
function safely(action: () => void): void {
  try {
    action();
  } catch {
    // nothing useful to do
  }
}

/** Counted once per page load, however many times the launcher remounts. */
let sessionCounted = false;

export interface LauncherDiscovery {
  /** True once the label should collapse to the circle. */
  readonly compact: boolean;
  /** True while the tip card should be on screen. */
  readonly hintVisible: boolean;
  /** Call when the assistant opens: it is no longer undiscovered. */
  readonly markOpened: () => void;
  /** Call when the person dismisses the hint. */
  readonly dismissHint: () => void;
  /** Pause the hint's countdown while keyboard focus is inside it. */
  readonly setHintPaused: (paused: boolean) => void;
}

export function useLauncherDiscovery(isOpen: boolean): LauncherDiscovery {
  const [state, setState] = useState<Discovery>(() => {
    const current = read();
    if (sessionCounted) return current;
    sessionCounted = true;
    safely(() => local.remove(RETIRED_HIDDEN_KEY));
    const counted = { ...current, sessions: current.sessions + 1 };
    safely(() => local.set(KEY, counted));
    return counted;
  });
  // idle → waiting for the delay, showing → on screen, done → gone for the session
  const [phase, setPhase] = useState<'idle' | 'showing' | 'done'>(() =>
    session.get<boolean>(HINT_SHOWN_KEY) === true ? 'done' : 'idle',
  );
  const [paused, setPaused] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  const eligible = !state.opened;

  // idle → showing, after the page has settled.
  useEffect(() => {
    if (phase !== 'idle' || !eligible) return undefined;
    const timer = window.setTimeout(() => {
      safely(() => session.set(HINT_SHOWN_KEY, true));
      setPhase('showing');
    }, HINT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [phase, eligible]);

  // showing → done, unless keyboard focus is inside the card. Pausing clears the
  // timer, so resuming gives the reader a fresh full countdown. The cleanup also
  // cancels it on unmount, on dismissal, and when the assistant opens.
  useEffect(() => {
    if (phase !== 'showing' || paused) return undefined;
    const timer = window.setTimeout(() => setPhase('done'), HINT_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [phase, paused]);

  const markOpened = useCallback((): void => {
    if (stateRef.current.opened) return;
    const next = { ...stateRef.current, opened: true };
    stateRef.current = next;
    setState(next);
    safely(() => local.set(KEY, next));
    safely(() => session.set(HINT_SHOWN_KEY, true));
    setPhase('done');
  }, []);

  const dismissHint = useCallback((): void => {
    safely(() => session.set(HINT_SHOWN_KEY, true));
    setPaused(false);
    setPhase('done');
  }, []);

  return {
    compact: state.opened || state.sessions > COMPACT_AFTER_SESSIONS || phase === 'done',
    hintVisible: phase === 'showing' && !isOpen,
    markOpened,
    dismissHint,
    setHintPaused: setPaused,
  };
}

/** Test seam: forget that this page load has been counted, and clear both stores. */
export function resetLauncherDiscoveryForTests(): void {
  sessionCounted = false;
  safely(() => local.remove(KEY));
  safely(() => session.remove(HINT_SHOWN_KEY));
  safely(() => local.remove(RETIRED_HIDDEN_KEY));
}
